import React, { useEffect } from 'react';
import {
	Autocomplete,
	Grid,
	TextField,
	Typography,
	Checkbox,
	Tooltip,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	InputAdornment,
	Button,
	FormControlLabel,
} from '@mui/material';
import { useState } from 'react';
import { Stack } from '@mui/system';
import { getProviders } from '../../actions/provider';
import { getSubBiases } from '../../actions/biases';
import { TOLLING_TYPES } from '../utils/lists';
import { getOrdersWithBiases } from '../../actions/orders';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid } from '@mui/x-data-grid';
import { columnStyleCell } from '../orderTracking/BiasTable';
import {
	getFloatValueMoney,
	getFullName,
	handleError,
	handleSuccess,
	handleWarning,
	money,
} from '../../helpers/utils';
import { getTodayDateMoment } from '../../helpers/hours';
import { useSelector } from 'react-redux';
import {
	getExternalTollingRequestInfo,
	handleGeneratEtrPDF,
	registerExternalTollingRequest,
	updateExternalTollingRequest,
} from '../../actions/externalTollingRequest';
import { useParams } from 'react-router-dom';
import Label from '../utils/Label';
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const initialStateForm = {
	tollingType: null,
	provider: null,
	numberOrder: '',
	currentBias: null,
	currentSubbiases: [],
};

export const ExternalTolling = ({
	edit = false,
	readonly = false,
	reception = false,
}) => {
	// Form
	const [form, setForm] = useState(initialStateForm);
	// Define state to know if it's loading or not
	const [order, setOrder] = useState({ biases: [] });
	const [subbiases, setSubbiases] = useState(null);
	const [providers, setProviders] = useState([]);
	const [rows, setRows] = useState([]);
	const [etr, setETR] = useState(null);
	const [selectedRow, setSelectedRow] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogInput, setDialogInput] = useState(0);
	const [currentRowEdit, setCurrentRowEdit] = useState(null);
	const [editingField, setEditingField] = useState('price');
	// URL params
	const { idETR = null } = useParams();

	// Navigation
	// const navigate = useNavigate();

	// Current user
	const { user } = useSelector((state) => state);

	useEffect(() => {
		if ((edit || readonly) && idETR && providers?.length > 0) {
			getExternalTollingRequestInfo(idETR).then(({ data }) => {
				setETR(data);
				setForm({
					...form,
					tollingType: TOLLING_TYPES.find(
						({ label }) => label === data.tolling_type
					),
					provider: data.provider,
				});
				setRows(data?.table_json?.data ?? []);
				localStorage.setItem(
					'lastPathPilu',
					location.href.split('/#/')[1]
				);
			});
		}
	}, [idETR, providers]);

	// Dialog
	const handleOpenDialogEditRow = (row = null, type = 'price') => {
		if (type === 'units') {
			setEditingField(type);
			setCurrentRowEdit(row);
			setDialogInput(row.units);
		} else if (type === 'notes') {
			setEditingField(type);
			setCurrentRowEdit(row);
			setDialogInput(row.notes);
		} else if (type === 'price') {
			setEditingField(type);
			setSelectedRow(row);
			setDialogInput(row.price);
		} else {
			setEditingField(type);
			setSelectedRow(null);
			setDialogInput('');
		}
		setOpenDialog(true);
	};

	const handleDialogClose = () => {
		setOpenDialog(false);
	};

	const handlePriceEdit = () => {
		const updatedRows = rows.map((row) =>
			row.id === selectedRow.id
				? {
						...row,
						price: dialogInput,
						total_price: parseFloat(dialogInput) * row.total_pieces,
				  }
				: row
		);
		setRows(updatedRows);
		setOpenDialog(false);
	};

	// Function to check form validity
	const isFormValid = () => {
		for (const key in form) {
			if (
				form[key] === null ||
				form[key] === undefined ||
				form[key].length === 0
			) {
				return false; // If any value is null, undefined, or has a length of 0, return false
			}
		}
		return true; // If all values are valid, return true
	};

	// State to manage button disabled status
	const [isButtonDisabled, setIsButtonDisabled] = useState(true);

	// Update button disabled status when form changes
	useEffect(() => {
		setIsButtonDisabled(!isFormValid());
	}, [form]);

	const customLocaleText = {
		noRowsLabel: 'No hay filas por mostrar al momento',
	};

	// Get max pieces length
	const findMaxPiecesLength = (array) => {
		return array.reduce((maxPiecesLength, obj) => {
			const currentPiecesLength = obj.pieces ? obj.pieces.length : 0;
			return Math.max(maxPiecesLength, currentPiecesLength);
		}, 0);
	};

	// Handle get PDF payload
	const getEtrPayload = () => {
		// Total props
		let totalPiecesGlobal = 0;
		let totalPriceGlobal = 0;

		// Get orders prop
		let orders = rows.map((item) => {
			let totalPiecesRow = 0;

			const pieces = item.subbiases.map((subbiasItem) => {
				// Increment total pieces number
				totalPiecesRow += subbiasItem.units;
				// Return piece object
				return {
					size: subbiasItem.size.name,
					quantity: subbiasItem.units,
					got_received: subbiasItem.howManyGotReceived,
				};
			});

			// Increment total pieces global
			totalPiecesGlobal += totalPiecesRow;
			// Increment total pieces price
			totalPriceGlobal += totalPiecesRow * item.price;

			return {
				order: item.order,
				departure_number: item.bias.key,
				description: item.notes,
				pieces,
				total_pieces: totalPiecesRow,
				unit_price: money(item.price),
				total_price: money(totalPiecesRow * item.price),
			};
		});

		// Get the max num of order pieces
		const maxOrderPieces = findMaxPiecesLength(orders);

		// Set the empty tds range
		orders.forEach((item) => {
			item.empty_tds_range = new Array(
				maxOrderPieces - item.pieces.length
			);
		});

		// PDF payload
		return {
			id: edit ? etr.pdf_payload.id : 0,
			is_exit: !reception,
			requestType: form.tollingType.label,
			// provider: form.provider.name, // TODO: add provider to the PDF template
			worker: getFullName(user),
			date: getTodayDateMoment(0, 'DD/MM/YYYY'),
			observations: dialogInput,
			orders: orders,
			total_pieces: totalPiecesGlobal,
			total_price: money(totalPriceGlobal), // This is info to format pdf
			total_cols: maxOrderPieces + 7, // Max from orders.pieces + 7
			total_cols_pieces: maxOrderPieces + 1, // Max from orders.pieces + 1
			total_cols_totals: maxOrderPieces + 4, // Max from orders.pieces + 4
		};
	};

	const whichIsDone = () => {
		if (edit && readonly && reception) {
			return true;
		}
		return false;
	};

	// Handle submit
	const handleSubmit = () => {
		// PDF payload
		let pdfPayload = getEtrPayload();

		// External Tolling Request payload
		const etrPayload = {
			is_done: whichIsDone(),
			description: pdfPayload.observations,
			total_pieces: pdfPayload.total_pieces,
			total_price: getFloatValueMoney(pdfPayload.total_price),
			pdf_payload: pdfPayload,
			table_json: { data: rows },
			tolling_type: form.tollingType.label,
			created_by: user.id,
			provider: form.provider.id,
		};

		if (edit) {
			const newApprovedValue = etr.approved
				? true
				: edit && user?.user_type?.id === 2
				? true
				: false;
			// Update pdf_payload
			updateExternalTollingRequest(etr.id, {
				...etrPayload,
				approved: newApprovedValue,
				is_done: etr?.is_done ? etr?.is_done : whichIsDone(),
			})
				.then(() => {
					// Success feedback message
					handleSuccess('¡Solicitud actualizada!');
					// Update pdf payload
					setETR({
						...etr,
						pdf_payload: pdfPayload,
						approved: newApprovedValue,
					});
					if (reception) {
						handleGeneratEtrPDF(pdfPayload);
					}
				})
				.catch((error) => {
					// Error feedback message
					handleError(error, 'No se pudo actualizar la solicitud.');
				});
		} else {
			// Create new ETR
			registerExternalTollingRequest(etrPayload)
				.then(({ data: newETR }) => {
					if (newETR) {
						// Success feedback message
						handleSuccess('¡Solicitud creada!');

						// Update pdf payload
						pdfPayload = {
							...pdfPayload,
							id: newETR.key,
						};

						// Reset biases and subbiases
						handleResetBiasesAndSubbiasesArray();
						// Reset form with initial state
						setForm(initialStateForm);
						// Reset rows
						setRows([]);
						// Close dialog
						handleDialogClose();

						// Update pdf_payload
						updateExternalTollingRequest(newETR.id, {
							...newETR,
							pdf_payload: pdfPayload,
						}).then(() => {
							// Download PDF
							handleGeneratEtrPDF(pdfPayload);
						});
					}
				})
				.catch((error) => {
					// Error feedback message
					handleError(
						error,
						'No se pudo agregar crear la solicitud.'
					);
				});
		}
	};

	// Reset current row to default values
	const handleResetCurrentRow = () => {
		setCurrentRowEdit(null);
		setDialogInput(0);
	};

	// Update units of the specific subbias row state
	const updateUnits = (row) => {
		// Make a copy of rows
		let newRows = [...rows];
		// Parse value to an integer
		const value = parseInt(dialogInput);
		let currentRow = newRows[row?.rowIndex];
		// Get current subbias row
		let currentSubbiasRow =
			newRows[row?.rowIndex].subbiases[row?.rowSubbiasIndex];
		// Get new units
		const newUnits = isNaN(value) ? 0 : value;
		// Set new total pieces (units)
		currentRow.total_pieces =
			row.total_pieces - currentSubbiasRow.units + newUnits;
		// Set new units
		currentSubbiasRow.units = newUnits;
		// Set new total price (units * price)
		currentRow.total_price = currentRow.total_pieces * currentRow.price;
		// Set old value
		currentSubbiasRow.oldValue = row.units;
		// Update rows
		setRows(newRows);
		// Reset current row
		handleResetCurrentRow();
		// Close dialog
		handleDialogClose();
	};

	// Update notes
	const updateNotes = (row) => {
		// Make a copy of rows
		let newRows = [...rows];
		// Get current subbias row
		newRows[row?.rowIndex].notes = dialogInput;
		// Update rows
		setRows(newRows);
		// Reset current row
		handleResetCurrentRow();
		// Close dialog
		handleDialogClose();
	};

	// Reset biases and subbiases
	const handleResetBiasesAndSubbiasesArray = () => {
		setOrder({ biases: [] });
		setSubbiases(null);
	};

	const handleUpdateSubbiasProp = (
		prop,
		rowIndex,
		rowSubbiasIndex,
		newValue
	) => {
		// Make a copy of rows
		let newRows = [...rows];
		// Get current row
		let currentRow = newRows[rowIndex].subbiases[rowSubbiasIndex];
		// Set old value units to the current value units
		currentRow[prop] = newValue;
		// Update rows
		setRows(newRows);
	};

	// Revert units of the specific subbias row state
	const handleRevertOldValueSubbiasUnit = (rowIndex, rowSubbiasIndex) => {
		// Make a copy of rows
		let newRows = [...rows];
		// Get current row
		let currentRow = newRows[rowIndex].subbiases[rowSubbiasIndex];
		// Set old value units to the current value units
		currentRow.units = currentRow.oldValue;
		// Delete old value units
		delete currentRow.oldValue;
		// Update rows
		setRows(newRows);
		// Reset current row
		handleResetCurrentRow();
	};

	const subtableCellStyle = {
		textAlign: 'center',
	};

	// Define columns
	const columns = [
		{
			field: 'col8',
			headerName: 'Acciones',
			width: 100,
			...columnStyleCell,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					{edit && (
						<Tooltip title='Eliminar'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='delete'
								color='primary'
								onClick={() =>
									setRows((old) =>
										old.filter((item) => item.id !== row.id)
									)
								}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</>
			),
		},
		{
			field: 'order',
			headerName: 'Pedido',
			width: 100,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.order}>
					<span className='table-cell-trucate'>{row.order}</span>
				</Tooltip>
			),
		},
		{
			field: 'bias',
			headerName: 'Partida',
			width: 300,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<Tooltip title={row.bias.key}>
					<span className='table-cell-trucate'>{`${row.bias.key}: ${row.bias.garment.name} (${row.bias.units} u.)`}</span>
				</Tooltip>
			),
		},
		{
			field: 'subbiases',
			headerName: 'Subpartidas',
			width: 350,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell style={subtableCellStyle}>
									Talla
								</TableCell>
								<TableCell style={subtableCellStyle}>
									Unidades
								</TableCell>
								{!readonly && (
									<TableCell style={subtableCellStyle}>
										Acción
									</TableCell>
								)}
								{reception && (
									<TableCell style={subtableCellStyle}>
										Recepción
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{row?.subbiases.map((itemRow, rowSubbiasIndex) => (
								<TableRow key={rowSubbiasIndex}>
									<TableCell style={subtableCellStyle}>
										T{itemRow?.size?.name ?? '-'}
									</TableCell>
									<TableCell style={subtableCellStyle}>
										{itemRow?.units ?? '-'}
									</TableCell>
									{!readonly && (
										<TableCell style={subtableCellStyle}>
											{' '}
											<Tooltip title='Editar'>
												<IconButton
													style={{ color: '#143f6c' }}
													aria-label='edit'
													color='primary'
													onClick={() => {
														handleOpenDialogEditRow(
															{
																...row,
																...itemRow,
																rowIndex:
																	rows.findIndex(
																		(
																			item
																		) =>
																			item.id ===
																			row.id
																	),
																rowSubbiasIndex,
															},
															'units'
														);
													}}
												>
													<EditIcon />
												</IconButton>
											</Tooltip>{' '}
											{itemRow?.oldValue && (
												<Tooltip
													title={`Regresar valor anterior: ${itemRow?.oldValue}`}
												>
													<IconButton
														style={{
															color: '#143f6c',
														}}
														aria-label='edit'
														color='primary'
														onClick={() => {
															const rowIndex =
																rows.findIndex(
																	(item) =>
																		item.id ===
																		row.id
																);
															handleRevertOldValueSubbiasUnit(
																rowIndex,
																rowSubbiasIndex
															);
														}}
													>
														<RestoreIcon />
													</IconButton>
												</Tooltip>
											)}
										</TableCell>
									)}
									{reception && (
										<TableCell style={subtableCellStyle}>
											<FormControlLabel
												control={
													<Checkbox
														disabled={etr?.is_done}
														checked={
															itemRow?.isComplete ??
															false
														}
														onChange={() => {
															const rowIndex =
																rows.findIndex(
																	(item) =>
																		item.id ===
																		row.id
																);
															handleUpdateSubbiasProp(
																'isComplete',
																rowIndex,
																rowSubbiasIndex,
																!itemRow?.isComplete
															);
															handleUpdateSubbiasProp(
																'howManyGotReceived',
																rowIndex,
																rowSubbiasIndex,
																itemRow.units
															);
														}}
													/>
												}
												label='Completo'
											/>
											{!itemRow?.isComplete && (
												<TextField
													disabled={etr?.is_done}
													label='Recibí'
													type='number'
													variant='standard'
													value={
														itemRow?.howManyGotReceived
													}
													onChange={({ target }) => {
														const rowIndex =
															rows.findIndex(
																(item) =>
																	item.id ===
																	row.id
															);
														let value = parseInt(
															target.value
														);
														value = isNaN(value)
															? ''
															: value;

														if (
															value >
															itemRow.units
														) {
															handleWarning(
																'No puede ser mayor que las unidades que esperas recibir.'
															);
															value = '';
														}
														if (
															value ===
															itemRow.units
														) {
															handleUpdateSubbiasProp(
																'isComplete',
																rowIndex,
																rowSubbiasIndex,
																true
															);
														}
														handleUpdateSubbiasProp(
															'howManyGotReceived',
															rowIndex,
															rowSubbiasIndex,
															value
														);
													}}
													size='small'
												/>
											)}
										</TableCell>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			),
		},
		{
			field: 'total_pieces',
			headerName: 'Total de unidades',
			width: 240,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>{row.total_pieces}</span>
			),
		},
		{
			field: 'price',
			headerName: 'Precio unitario',
			width: 240,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<>
					<span className='table-cell-trucate'>
						{money(parseFloat(row.price))}
						{user?.user_type?.id === 2 && !readonly && (
							<Tooltip title='Editar precio'>
								<IconButton
									style={{
										color: '#143f6c',
										marginLeft: '5px',
									}}
									color='primary'
									onClick={() => handleOpenDialogEditRow(row)}
								>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					</span>
				</>
			),
		},
		{
			field: 'total_price',
			headerName: 'Precio total',
			width: 240,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{money(parseFloat(row.total_price))}
				</span>
			),
		},
		{
			field: 'notes',
			headerName: 'Descripción',
			width: 400,
			...columnStyleCell,
			renderCell: ({ row }) => (
				<>
					<span className='table-cell-trucate'>
						{row.notes}{' '}
						{!readonly && (
							<Tooltip title='Editar descripción'>
								<IconButton
									style={{
										color: '#143f6c',
										marginLeft: '5px',
									}}
									color='primary'
									onClick={() => {
										const rowIndex = rows.findIndex(
											(item) => item.id === row.id
										);
										handleOpenDialogEditRow(
											{
												...row,
												rowIndex,
											},
											'notes'
										);
									}}
								>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					</span>
				</>
			),
		},
	];

	const handleAddRow = (removeOrder = false) => {
		const totalPieces = form.currentSubbiases?.reduce(
			(sum, item) => sum + item.units,
			0
		);
		setRows([
			...rows,
			{
				id: rows?.length + 1,
				order: form.numberOrder,
				bias: form.currentBias,
				subbiases: form.currentSubbiases,
				price: 0,
				total_price: 0,
				total_pieces: totalPieces,
				notes: '',
			},
		]);
		setForm({
			...form,
			currentBias: null,
			currentSubbiases: [],
			numberOrder: removeOrder ? '' : form.numberOrder,
			// notes: '',
		});
	};

	useEffect(() => {
		getProviders(null, 100).then(({ data }) => {
			setProviders(data.results);
		});
	}, []);

	useEffect(() => {
		if (form?.numberOrder?.length >= 5) {
			getOrdersWithBiases(form?.numberOrder).then(({ data }) => {
				handleSuccess('Partidas actualizadas', true, 3, 'top-right');
				setOrder(data);
			});
		}
	}, [form?.numberOrder]);

	useEffect(() => {
		if (form?.currentBias !== null) {
			getSubBiases(form?.currentBias.id).then(({ data }) => {
				setSubbiases(data.results);
			});
		}
	}, [form?.currentBias]);

	const TextFieldComponent = (params, label) => (
		<TextField
			fullWidth
			required
			margin='normal'
			{...params}
			label={label}
		/>
	);

	return (
		<div style={{ padding: '25px' }}>
			{/* Edit price */}
			<Dialog
				open={openDialog}
				onClose={handleDialogClose}
				fullWidth={
					editingField === 'notes' || editingField === 'finalNotes'
				}
			>
				<DialogTitle>
					{editingField === 'finalNotes' ? 'Agregar' : 'Editar'}{' '}
					{editingField === 'notes' && 'descripción'}
					{editingField === 'units' && 'unidades'}
					{editingField === 'price' && 'precio'}
					{editingField === 'finalNotes' && 'observaciones'}
				</DialogTitle>
				<DialogContent>
					{editingField === 'finalNotes' && (
						<Grid container spacing={2} className='mt-2'>
							<Grid item xs={12}>
								<TextField
									disabled={readonly}
									value={dialogInput}
									label='Observaciones'
									fullWidth
									multiline
									minRows={3}
									onChange={({ target }) =>
										setDialogInput(target.value)
									}
								/>
							</Grid>
						</Grid>
					)}
					{currentRowEdit && (
						<Grid container spacing={2} className='mt-2'>
							{editingField === 'notes' ? (
								<Grid item xs={12}>
									<TextField
										disabled={readonly}
										value={dialogInput}
										label='Descripción'
										fullWidth
										multiline
										minRows={3}
										onChange={({ target }) =>
											setDialogInput(target.value)
										}
									/>
								</Grid>
							) : (
								<>
									<Grid item xs={6}>
										<TextField
											value={currentRowEdit?.size?.name}
											label='Talla'
											disabled
										/>
									</Grid>

									<Grid item xs={6}>
										<TextField
											disabled={readonly}
											type='number'
											label='Unidades'
											value={dialogInput}
											onChange={({ target }) =>
												setDialogInput(target.value)
											}
										/>
									</Grid>
								</>
							)}
						</Grid>
					)}
					{editingField === 'price' && (
						<TextField
							disabled={readonly}
							value={dialogInput}
							onChange={({ target }) =>
								setDialogInput(target.value)
							}
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<AttachMoneyIcon />
									</InputAdornment>
								),
							}}
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						color='error'
						onClick={() => {
							setCurrentRowEdit(null);
							handleDialogClose();
						}}
					>
						Cancelar
					</Button>
					<Button
						disabled={dialogInput?.length === 0}
						onClick={() => {
							// Editing subbias item or notes
							if (currentRowEdit) {
								if (editingField === 'notes') {
									updateNotes(currentRowEdit);
								} else {
									updateUnits(currentRowEdit);
								}
							}
							// Editing final notes to submit final form
							else if (editingField === 'finalNotes') {
								handleSubmit();
							}
							// Editing row price
							else {
								handlePriceEdit();
							}
						}}
					>
						Guardar
					</Button>
				</DialogActions>
			</Dialog>

			<Stack
				direction='column'
				alignItems='flex-start'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 10px 0',
					width: '100%',
				}}
			>
				<div
					style={{
						width: '100%',
						display: 'flex',
						flexDirection: 'row',
					}}
				>
					<Typography
						variant='h4'
						gutterBottom
						style={{
							width: '450px',
							border: '1px solid transparent',
						}}
					>
						Solicitud de maquila externa{' '}
					</Typography>
					<div
						style={{
							paddingTop: '10px',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							width: '200px',
							border: '1px solid transparent',
						}}
					>
						{etr?.approved && (
							<Tooltip title='Listo para recibir y completar'>
								<Label color='success'>Aprobado</Label>
							</Tooltip>
						)}{' '}
						{etr?.is_done && (
							<Tooltip title='Ya no hay más acciones por hacer'>
								<Label color='success'>Completada</Label>
							</Tooltip>
						)}
					</div>
				</div>
				<Typography variant='p' gutterBottom>
					Complete el formulario para crear una solicitud de maquila
					externa.
				</Typography>
			</Stack>
			<hr />
			<Grid container spacing={2} className='mt-2'>
				<Grid item xs={4}>
					<Autocomplete
						disabled={readonly}
						required
						style={{ marginTop: '-17px' }}
						fullWidth
						value={form?.tollingType}
						onChange={(_, value) => {
							setForm({ ...form, tollingType: value });
						}}
						options={TOLLING_TYPES}
						getOptionLabel={(option) => option.label}
						renderInput={(params) =>
							TextFieldComponent(params, 'Tipo de maquila')
						}
					/>
				</Grid>

				<Grid item xs={4}>
					<Autocomplete
						disabled={readonly}
						required
						style={{ marginTop: '-17px' }}
						fullWidth
						value={form?.provider}
						onChange={(_, value) => {
							setForm({ ...form, provider: value });
						}}
						options={providers || []}
						getOptionLabel={(option) => option.name}
						renderInput={(params) =>
							TextFieldComponent(params, 'Proveedor')
						}
					/>
				</Grid>

				{!readonly && (
					<Grid item xs={4}>
						<TextField
							disabled={readonly}
							required
							fullWidth
							label='Número de orden'
							value={form?.numberOrder}
							onChange={({ target }) =>
								setForm({
									...form,
									numberOrder: target.value,
								})
							}
						/>
					</Grid>
				)}

				{order?.biases?.length > 0 && (
					<Grid item xs={4}>
						<Autocomplete
							disabled={readonly}
							required
							style={{ marginTop: '-17px' }}
							fullWidth
							value={form?.currentBias}
							onChange={(_, value) => {
								setForm({ ...form, currentBias: value });
							}}
							options={order?.biases}
							getOptionLabel={(option) =>
								`${option.key}: ${option.garment.name} (${option.units} u.)`
							}
							renderInput={(params) =>
								TextFieldComponent(params, 'Partidas')
							}
						/>
					</Grid>
				)}

				{subbiases?.length > 0 && (
					<Grid item xs={8}>
						<Autocomplete
							disabled={readonly}
							required
							multiple
							fullWidth
							value={form?.currentSubbiases}
							onChange={(_, value) => {
								setForm({ ...form, currentSubbiases: value });
							}}
							disableCloseOnSelect
							options={subbiases}
							getOptionLabel={(option) => option.key}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{option.key}
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label='Subpartidas'
									placeholder='Subpartidas'
								/>
							)}
						/>
					</Grid>
				)}
				{(readonly && !reception) ||
					(etr?.is_done && (
						<Grid item xs={4}>
							{etr?.pdf_payload && (
								<Button
									fullWidth
									disabled={rows?.length === 0}
									color='error'
									variant='outlined'
									size='large'
									onClick={() => {
										handleGeneratEtrPDF(etr.pdf_payload);
									}}
								>
									PDF
								</Button>
							)}
						</Grid>
					))}
			</Grid>

			{(readonly && !reception) || etr?.is_done ? (
				<></>
			) : (
				<Grid container spacing={2} className='mt-2'>
					<Grid item xs={3}>
						<Button
							fullWidth
							disabled={isButtonDisabled || readonly}
							size='large'
							variant='outlined'
							onClick={() => handleAddRow(false)}
						>
							Agregar
						</Button>
					</Grid>
					<Grid item xs={3}>
						<Button
							fullWidth
							disabled={isButtonDisabled || readonly}
							size='large'
							variant='outlined'
							onClick={() => {
								handleAddRow(true);
								handleResetBiasesAndSubbiasesArray();
							}}
						>
							Agregar y quitar orden
						</Button>
					</Grid>
					{reception && (
						<Grid item xs={3}>
							<Button
								fullWidth
								size='large'
								variant='outlined'
								color='success'
								onClick={() => {
									handleSubmit();
								}}
							>
								Completar solicitud
							</Button>
						</Grid>
					)}
					{edit && (
						<Grid item xs={3}>
							<Button
								fullWidth
								disabled={rows?.length === 0}
								color='error'
								variant='outlined'
								size='large'
								onClick={() => {
									handleGeneratEtrPDF(etr.pdf_payload);
								}}
							>
								PDF
							</Button>
						</Grid>
					)}
					<Grid item xs={edit ? 3 : 6}>
						<Button
							fullWidth
							disabled={rows?.length === 0 || readonly}
							color='success'
							variant='outlined'
							size='large'
							onClick={() => {
								if (edit) {
									handleSubmit();
								} else {
									handleOpenDialogEditRow(null, 'finalNotes');
								}
							}}
						>
							{edit
								? user?.user_type?.id === 2
									? 'Actualizar y aprobar'
									: 'Actualizar'
								: 'Guardar'}
						</Button>
					</Grid>
				</Grid>
			)}

			{/* DATAGRID */}
			<div style={{ height: 400, width: '100%', margin: '0 auto' }}>
				<DataGrid
					rows={rows}
					style={{ height: '600px', margin: '10px 0 0 0' }}
					getRowHeight={() => 'auto'}
					columns={columns}
					pageSize={5}
					localeText={customLocaleText}
				/>
			</div>
		</div>
	);
};
